<div class="ort-ops">
  <header class="ort-ops__topbar">
    <div class="ort-ops__brand">
      <div class="ort-ops__brand-icon"><i class="fas fa-file-medical-alt"></i></div>
      <div>
        <div class="ort-ops__brand-title">Medical Records</div>
        <div class="ort-ops__brand-subtitle">Live-synced action items &amp; clinical documentation</div>
      </div>
    </div>

    <div class="mode-toggle">
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="mode === 'live'"
        (click)="setMode('live')"
      >
        <i class="fas fa-bolt"></i> Live
      </button>
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="mode === 'static'"
        (click)="setMode('static')"
      >
        <i class="fas fa-hand-pointer"></i> Static
      </button>
    </div>

    <div class="ort-ops__patient" *ngIf="currentPatientId; else waitingPatient">
      <div class="ort-ops__patient-avatar" [style.background]="avatarColor">{{ initials }}</div>
      <div class="ort-ops__patient-info">
        <div class="ort-ops__patient-name">{{ currentPatientName || 'Unknown Patient' }}</div>
        <div class="ort-ops__patient-meta" *ngIf="currentPatientMrn">MRN {{ currentPatientMrn }}</div>
      </div>
      <span class="ort-ops__now-badge" *ngIf="mode === 'live'"><span class="live-dot"></span> Now Processing</span>
      <span class="ort-ops__pinned-badge" *ngIf="mode === 'static'"><i class="fas fa-thumbtack"></i> Pinned</span>
    </div>
    <ng-template #waitingPatient>
      <div class="ort-ops__patient ort-ops__patient--empty">
        <i class="fas fa-hourglass-half"></i>
        {{ mode === 'live' ? 'Waiting for activity…' : 'Click a patient to pin' }}
      </div>
    </ng-template>

    <button
      type="button"
      class="ort-ops__print-btn"
      *ngIf="printableDocumentCount"
      (click)="openPrintModal()"
      title="Print documents"
    >
      <i class="fas fa-print"></i> Print
    </button>

    <div class="ort-ops__status">
      <span class="live-dot"></span>
      Live
      <span class="ort-ops__updated">Updated {{ formatTime(lastUpdated) }}</span>
    </div>

    <button type="button" class="ort-ops__close-btn" (click)="closeTab()" title="Close Tab">
      <i class="fas fa-times"></i>
    </button>
  </header>

  <!-- Rendered inline only in the Clinical Documentation panel (below), via ngTemplateOutlet —
       the Files panel lists Medical Necessity as a plain file row (View/Download) instead, like
       its other file types, so this content isn't shown twice. -->
  <ng-template #medicalNecessitySection>
    <div class="doc-section">
      <div class="doc-section__heading"><i class="fas fa-shield-alt"></i> Medical Necessity</div>

      <div class="doctor-notes-card" *ngFor="let item of medicalNecessityItems; trackBy: trackByMedicalNecessityId">
        <div class="doctor-notes-card__header">
          <span><i class="fas fa-calendar-day"></i> {{ formatDictationTime(item.dictationTime || item.created_at) }}</span>
          <span *ngIf="item.order?.order_number">Order #{{ item.order.order_number }}</span>
        </div>
        <div class="doctor-notes-card__body">
          <span
            class="doctor-notes-card__mn-badge"
            [ngClass]="{
              'doctor-notes-card__mn-badge--approved': medicalNecessityIndicator(item) === 'approved',
              'doctor-notes-card__mn-badge--missing': medicalNecessityIndicator(item) === 'missing',
              'doctor-notes-card__mn-badge--not-approved': medicalNecessityIndicator(item) === 'not-approved'
            }"
          >
            {{ medicalNecessityIndicatorLabel(item) }}
          </span>

          <div class="doctor-notes-card__section">
            <h4>Action Item</h4>
            <p>{{ item.action_item }}</p>
          </div>

          <div class="doctor-notes-card__section" *ngIf="item.cptCodes.length">
            <h4>CPT Codes</h4>
            <p *ngFor="let code of item.cptCodes">
              <strong>{{ code.code }}</strong><ng-container *ngIf="code.reason"> — {{ code.reason }}</ng-container>
            </p>
          </div>

          <div class="doctor-notes-card__section" *ngFor="let row of item.coverageDetails">
            <h4>{{ row.label }}</h4>
            <div
              class="doctor-notes-card__coverage-block"
              *ngFor="let block of row.blocks"
              [ngClass]="{
                'doctor-notes-card__coverage-block--numbered': block.type === 'numbered',
                'doctor-notes-card__coverage-block--bullet': block.type === 'bullet'
              }"
            >
              <span class="doctor-notes-card__coverage-number" *ngIf="block.type === 'numbered'">{{ block.number }}</span>
              <span class="doctor-notes-card__coverage-bullet" *ngIf="block.type === 'bullet'">&bull;</span>
              <p>
                <span class="doctor-notes-card__coverage-label" *ngIf="block.label">{{ block.label }}</span>
                {{ block.text }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="doc-section__empty" *ngIf="!medicalNecessityItems.length">
        <i class="fas fa-folder-open"></i> No Medical Necessity documentation for this patient.
      </div>
    </div>
  </ng-template>

  <!-- Print modal — lists every document currently loaded in Clinical Documentation, each with
       its own Print button, plus a "Print All" action for everything at once. -->
  <ng-template #printDocumentsModal let-modal>
    <div class="print-modal">
      <div class="print-modal__header">
        <div class="print-modal__header-left">
          <div class="print-modal__icon"><i class="fas fa-print"></i></div>
          <div>
            <div class="print-modal__title">Print Clinical Documentation</div>
            <div class="print-modal__subtitle">
              {{ currentPatientName || 'Unknown Patient' }}<span *ngIf="currentPatientMrn"> · MRN {{ currentPatientMrn }}</span>
            </div>
          </div>
        </div>
        <button type="button" class="print-modal__close" (click)="modal.close()" title="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="print-modal__body">
        <div class="print-modal__empty" *ngIf="!printableDocumentCount">
          <i class="fas fa-print"></i> No documents available to print yet.
        </div>

        <ng-container *ngIf="medicalNecessityItems.length">
          <div class="print-modal__group-title"><i class="fas fa-shield-alt"></i> Medical Necessity</div>
          <div class="print-modal__row" *ngFor="let item of medicalNecessityItems; trackBy: trackByMedicalNecessityId">
            <div class="print-modal__row-body">
              <div class="print-modal__row-label">{{ item.action_item }}</div>
              <div class="print-modal__row-meta">{{ formatDictationTime(item.dictationTime || item.created_at) }}</div>
            </div>
            <button type="button" class="print-modal__print-btn" (click)="printMedicalNecessity(item)">
              <i class="fas fa-print"></i> Print
            </button>
          </div>
        </ng-container>

        <ng-container *ngIf="ortMasters.length">
          <div class="print-modal__group-title"><i class="fas fa-file-medical-alt"></i> Medical Records</div>
          <div class="print-modal__row" *ngFor="let note of ortMasters; trackBy: trackByOrtMasterId">
            <div class="print-modal__row-body">
              <div class="print-modal__row-label">{{ formatOrtDate(note) }}</div>
            </div>
            <button type="button" class="print-modal__print-btn" (click)="printOrtMaster(note)">
              <i class="fas fa-print"></i> Print
            </button>
          </div>
        </ng-container>

        <ng-container *ngIf="doctorNotes.length">
          <div class="print-modal__group-title"><i class="fas fa-notes-medical"></i> Doctor Notes</div>
          <div class="print-modal__row" *ngFor="let note of doctorNotes; trackBy: trackByDoctorNoteId">
            <div class="print-modal__row-body">
              <div class="print-modal__row-label">{{ formatDictationTime(note.created_at) }}</div>
            </div>
            <button type="button" class="print-modal__print-btn" (click)="printDoctorNote(note)">
              <i class="fas fa-print"></i> Print
            </button>
          </div>
        </ng-container>
      </div>

      <div class="print-modal__footer" *ngIf="printableDocumentCount">
        <span class="print-modal__count">{{ printableDocumentCount }} document{{ printableDocumentCount === 1 ? '' : 's' }}</span>
        <button type="button" class="print-modal__print-all-btn" (click)="printAll()">
          <i class="fas fa-print"></i> Print All
        </button>
      </div>
    </div>
  </ng-template>

  <div class="ort-ops__grid">
    <!-- Left column, row 1 (35%): Patients — most recently active patient on top, infinite scroll. -->
    <section class="ort-ops__panel ort-ops__panel--patients">
      <div class="ort-ops__panel-header">
        <span><i class="fas fa-user-injured"></i> Patients</span>
        <span class="ort-ops__panel-hint" *ngIf="mode === 'static'">Click to pin</span>
        <span class="ort-ops__count" *ngIf="patients.length">{{ patients.length }}</span>
      </div>
      <div class="ort-ops__panel-body" (scroll)="onLogsScroll($event)">
        <div class="ort-ops__empty" *ngIf="logsLoading && !patients.length">
          <i class="fas fa-circle-notch fa-spin"></i> Loading…
        </div>
        <div class="ort-ops__empty" *ngIf="!logsLoading && !patients.length">
          <i class="fas fa-inbox"></i> No activity yet.
        </div>

        <div
          class="patient-item"
          *ngFor="let row of patients; trackBy: trackByPatientId"
          [class.patient-item--clickable]="mode === 'static'"
          [class.patient-item--selected]="mode === 'static' && row.patientId === currentPatientId"
          [class.patient-item--current]="mode === 'live' && row.patientId === currentPatientId"
          (click)="selectPatient(row)"
        >
          <div class="patient-item__avatar" [style.background]="avatarColorFor(row.patientId)">
            {{ initialsFor(row.patientName) }}
          </div>
          <div class="patient-item__body">
            <div class="patient-item__name">{{ row.patientName || 'Unknown Patient' }}</div>
            <div class="patient-item__meta">
              <span *ngIf="row.patientMrn">MRN {{ row.patientMrn }}</span>
              <span class="patient-item__time">{{ formatDictationTime(row.lastActivityAt) }}</span>
            </div>
          </div>
          <span class="patient-item__current-badge" *ngIf="row.patientId === currentPatientId">
            <span class="live-dot" *ngIf="mode === 'live'"></span>
            <i class="fas fa-thumbtack" *ngIf="mode === 'static'"></i>
          </span>
        </div>

        <div class="ort-ops__loading-more" *ngIf="logsLoadingMore">
          <i class="fas fa-circle-notch fa-spin"></i> Loading older activity…
        </div>
      </div>
    </section>

    <!-- Left column, row 2 (35%): Action Items — read-only, no selection. -->
    <section class="ort-ops__panel ort-ops__panel--items">
      <div class="ort-ops__panel-header">
        <span><i class="fas fa-tasks"></i> Action Items</span>
        <span class="ort-ops__count" *ngIf="total">{{ total }}</span>
      </div>
      <div class="ort-ops__panel-body" (scroll)="onActionItemsScroll($event)">
        <div class="ort-ops__empty" *ngIf="!currentPatientId">
          <i class="fas fa-hourglass-half"></i> No patient in focus yet.
        </div>
        <div class="ort-ops__empty" *ngIf="currentPatientId && actionItemsLoading && !actionItems.length">
          <i class="fas fa-circle-notch fa-spin"></i> Loading…
        </div>
        <div class="ort-ops__empty" *ngIf="currentPatientId && !actionItemsLoading && !actionItems.length">
          <i class="fas fa-clipboard-check"></i> No action items found for this patient.
        </div>

        <div class="ort-item" *ngFor="let item of actionItems; trackBy: trackByActionItemId">
          <i
            class="fas ort-item__status"
            [ngClass]="{
              'fa-check-circle ort-item__status--accepted': actionItemStatus(item) === 'accepted',
              'fa-times-circle ort-item__status--rejected': actionItemStatus(item) === 'rejected',
              'fa-clock ort-item__status--pending': actionItemStatus(item) === 'pending'
            }"
          ></i>
          <div class="ort-item__body">
            <div class="ort-item__text">{{ item.action_item }}</div>
            <div class="ort-item__time">{{ formatDictationTime(item.dictation_created_at || item.created_at) }}</div>
            <span
              *ngIf="medicalNecessityIndicator(item) as mnStatus"
              class="ort-item__mn-indicator"
              [ngClass]="{
                'ort-item__mn-indicator--approved': mnStatus === 'approved',
                'ort-item__mn-indicator--missing': mnStatus === 'missing',
                'ort-item__mn-indicator--not-approved': mnStatus === 'not-approved'
              }"
            >
              <span class="ort-item__mn-dot"></span>
              {{ medicalNecessityIndicatorLabel(item) }}
            </span>
          </div>
        </div>

        <div class="ort-ops__loading-more" *ngIf="actionItemsLoadingMore">
          <i class="fas fa-circle-notch fa-spin"></i> Loading more action items…
        </div>
      </div>
    </section>

    <!-- Left column, row 3 (30%): Files — same as Live Operations' Files panel, but covering
         the patient's full dictation history rather than just today's. -->
    <section class="ort-ops__panel ort-ops__panel--files">
      <div class="ort-ops__panel-header">
        <span><i class="fas fa-folder-open"></i> Files</span>
      </div>
      <div class="ort-ops__panel-body" (scroll)="onFilesScroll($event)">
        <div class="ort-ops__empty" *ngIf="!currentPatientId">
          <i class="fas fa-folder-open"></i> No patient in focus yet.
        </div>
        <div class="ort-ops__empty" *ngIf="currentPatientId && filesLoading && !filesDictations.length">
          <i class="fas fa-circle-notch fa-spin"></i> Loading…
        </div>
        <div class="ort-ops__empty" *ngIf="currentPatientId && !filesLoading && !filesDictations.length && !reportFileRows.length">
          <i class="fas fa-folder-open"></i> No files recorded for this patient yet.
        </div>

        <!-- Imaging & Operative reports — patient-level, not tied to any one dictation, so
             they get their own combined card rather than slotting into the per-dictation ones. -->
        <div class="file-card" *ngIf="reportFileRows.length">
          <div class="file-card__header">
            <span><i class="fas fa-file-medical-alt"></i> Imaging &amp; Operative Reports</span>
          </div>

          <ul class="file-list">
            <li class="file-list__row" *ngFor="let row of reportFileRows; trackBy: trackByFileRowKey">
              <i class="fas file-list__icon" [ngClass]="row.icon"></i>
              <div class="file-list__body">
                <div class="file-list__label">{{ row.label }}</div>
                <div class="file-list__subtitle" *ngIf="row.subtitle">{{ row.subtitle }}</div>
              </div>
              <button type="button" class="file-list__btn" (click)="row.view()" title="View {{ row.label }}">
                <i class="fas fa-eye"></i>
              </button>
              <button type="button" class="file-list__btn" (click)="row.download()" title="Download {{ row.label }}">
                <i class="fas fa-download"></i>
              </button>
            </li>
          </ul>
        </div>

        <div class="file-card" *ngFor="let d of filesDictations; trackBy: trackByFileDictationId">
          <div class="file-card__header">
            <span><i class="fas fa-clock"></i> {{ formatDictationTime(d.created_at) }}</span>
            <span *ngIf="d.provider">{{ d.provider.description }}</span>
          </div>

          <ul class="file-list">
            <li class="file-list__row" *ngFor="let row of d.fileRows; trackBy: trackByFileRowKey">
              <i class="fas file-list__icon" [ngClass]="row.icon"></i>
              <div class="file-list__body">
                <div class="file-list__label">{{ row.label }}</div>
                <div class="file-list__subtitle" *ngIf="row.subtitle">{{ row.subtitle }}</div>
              </div>
              <button type="button" class="file-list__btn" (click)="row.view()" title="View {{ row.label }}">
                <i class="fas fa-eye"></i>
              </button>
              <button
                type="button"
                class="file-list__btn"
                [disabled]="row.itemId && generatingPdfForItemId === row.itemId"
                (click)="row.download()"
                title="Download {{ row.label }} as PDF"
              >
                <i class="fas" [ngClass]="row.itemId && generatingPdfForItemId === row.itemId ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
              </button>
            </li>
          </ul>

          <p class="file-card__empty" *ngIf="!d.fileRows.length">No files available for this dictation yet.</p>
        </div>

        <div class="ort-ops__loading-more" *ngIf="filesLoadingMore">
          <i class="fas fa-circle-notch fa-spin"></i> Loading older files…
        </div>
      </div>
    </section>

    <!-- Right column (70%): Clinical Documentation — ORT Masters + Doctor Notes, stacked, for
         the current/pinned patient. -->
    <section class="ort-ops__panel ort-ops__panel--records">
      <div class="ort-ops__panel-header">
        <span><i class="fas fa-folder-open"></i> Clinical Documentation</span>
        <span class="ort-ops__source-badge" *ngIf="currentPatientId">Last 6 Months</span>
      </div>

      <div class="ort-ops__panel-body ort-ops__panel-body--doc">
        <div class="ort-ops__empty" *ngIf="!currentPatientId">
          <i class="fas fa-file-medical-alt"></i> No patient in focus yet.
        </div>
        <div class="ort-ops__empty" *ngIf="currentPatientId && rightPanelLoading">
          <i class="fas fa-circle-notch fa-spin"></i> Loading documentation…
        </div>

        <ng-container *ngIf="currentPatientId && !rightPanelLoading">
          <!-- Medical Necessity section -->
          <ng-container [ngTemplateOutlet]="medicalNecessitySection"></ng-container>

          <!-- ORT Masters section -->
          <div class="doc-section">
            <div class="doc-section__heading"><i class="fas fa-file-medical-alt"></i> Medical Records</div>

            <!-- Each is a full clinical HTML record, rendered sandboxed -->
            <div class="ort-master-card" *ngFor="let note of ortMasters; trackBy: trackByOrtMasterId">
              <div class="ort-master-card__header">
                <span><i class="fas fa-calendar-day"></i> {{ formatOrtDate(note) }}</span>
              </div>
              <iframe
                class="ort-master-card__frame"
                [srcdoc]="note.safeMasterNotes"
                sandbox=""
                scrolling="yes"
              ></iframe>
            </div>

            <div class="doc-section__empty" *ngIf="!ortMasters.length">
              <i class="fas fa-folder-open"></i> No Medical Records in the last 6 months.
            </div>
          </div>

          <!-- MRI Reports section — PDF only, the AI-extracted content column is never shown -->
          <div class="doc-section">
            <div class="doc-section__heading"><i class="fas fa-magnet"></i> MRI Reports</div>

            <div class="ort-master-card" *ngFor="let report of mriReports; trackBy: trackByReportId">
              <div class="ort-master-card__header">
                <div class="ort-master-card__header-left">
                  <span><i class="fas fa-calendar-day"></i> {{ formatReportDate(report) }}</span>
                  <span *ngIf="mriReportSubtitle(report)">{{ mriReportSubtitle(report) }}</span>
                </div>
                <a class="ort-master-card__header-link" [href]="report.image_url" target="_blank" rel="noopener" title="Open PDF in new tab">
                  <i class="fas fa-external-link-alt"></i> Open
                </a>
              </div>
              <iframe class="ort-master-card__frame" [src]="report.safePdfUrl" scrolling="yes"></iframe>
            </div>

            <div class="doc-section__empty" *ngIf="!mriReports.length">
              <i class="fas fa-folder-open"></i> No MRI Reports in the last 6 months.
            </div>
          </div>

          <!-- CT Reports section -->
          <div class="doc-section">
            <div class="doc-section__heading"><i class="fas fa-x-ray"></i> CT Reports</div>

            <div class="ort-master-card" *ngFor="let report of ctReports; trackBy: trackByReportId">
              <div class="ort-master-card__header">
                <div class="ort-master-card__header-left">
                  <span><i class="fas fa-calendar-day"></i> {{ formatReportDate(report) }}</span>
                  <span *ngIf="ctReportSubtitle(report)">{{ ctReportSubtitle(report) }}</span>
                </div>
                <a class="ort-master-card__header-link" [href]="report.image_url" target="_blank" rel="noopener" title="Open PDF in new tab">
                  <i class="fas fa-external-link-alt"></i> Open
                </a>
              </div>
              <iframe class="ort-master-card__frame" [src]="report.safePdfUrl" scrolling="yes"></iframe>
            </div>

            <div class="doc-section__empty" *ngIf="!ctReports.length">
              <i class="fas fa-folder-open"></i> No CT Reports in the last 6 months.
            </div>
          </div>

          <!-- Operative Reports section -->
          <div class="doc-section">
            <div class="doc-section__heading"><i class="fas fa-user-md"></i> Operative Reports</div>

            <div class="ort-master-card" *ngFor="let report of operativeReports; trackBy: trackByReportId">
              <div class="ort-master-card__header">
                <div class="ort-master-card__header-left">
                  <span><i class="fas fa-calendar-day"></i> {{ formatReportDate(report) }}</span>
                  <span *ngIf="report.notes_name">{{ report.notes_name }}</span>
                </div>
                <a class="ort-master-card__header-link" [href]="report.image_url" target="_blank" rel="noopener" title="Open PDF in new tab">
                  <i class="fas fa-external-link-alt"></i> Open
                </a>
              </div>
              <iframe class="ort-master-card__frame" [src]="report.safePdfUrl" scrolling="yes"></iframe>
            </div>

            <div class="doc-section__empty" *ngIf="!operativeReports.length">
              <i class="fas fa-folder-open"></i> No Operative Reports in the last 6 months.
            </div>
          </div>

          <!-- Doctor Notes section -->
          <div class="doc-section">
            <div class="doc-section__heading"><i class="fas fa-notes-medical"></i> Doctor Notes</div>

            <div class="doctor-notes-card" *ngFor="let note of doctorNotes; trackBy: trackByDoctorNoteId">
              <div class="doctor-notes-card__header">
                <span><i class="fas fa-calendar-day"></i> {{ formatDictationTime(note.created_at) }}</span>
              </div>
              <div class="doctor-notes-card__body">
                <div class="doctor-notes-card__section" *ngIf="hasNoteSection(note.history_of_present_illness)">
                  <h4>History Of Present Illness</h4>
                  <p>{{ note.history_of_present_illness }}</p>
                </div>
                <div class="doctor-notes-card__section" *ngIf="hasNoteSection(note.physical_examination)">
                  <h4>Physical Examination</h4>
                  <p>{{ note.physical_examination }}</p>
                </div>
                <div class="doctor-notes-card__section" *ngIf="hasNoteSection(note.diagnostics)">
                  <h4>Diagnostics</h4>
                  <p>{{ note.diagnostics }}</p>
                </div>
                <div class="doctor-notes-card__section" *ngIf="hasNoteSection(note.assessment_plan)">
                  <h4>Assessment / Plan</h4>
                  <p>{{ note.assessment_plan }}</p>
                </div>
              </div>
            </div>

            <div class="doc-section__empty" *ngIf="!doctorNotes.length">
              <i class="fas fa-folder-open"></i> No Doctor Notes in the last 6 months.
            </div>
          </div>
        </ng-container>
      </div>
    </section>
  </div>
</div>
